import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Tooltip,
  Chip,
  Stack,
  Paper,
  Alert,
  CircularProgress
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { areIntervalsOverlapping } from 'date-fns';
import { format, addDays } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useTechnicians } from '../../contexts/TechniciansContext';
import { useSkills } from '../../contexts/SkillsContext';
import { useAbsences } from '../../contexts/AbsencesContext';
import { getSkillLevelShortLabel, getSkillLevelColor } from '../../constants/skillLevels';
import { formatLocalDateToISO } from '../../utils/dateUtils';
// TODO: Migrer vers les contextes API
// import { absencesDB } from '../../db/database';

const TechnicianAssignment = ({ 
  technicians,
  skillId,
  weekData,
  onAssign,
  onRemove,
  currentAssignment,
  schedule,
  onCalls,
  skills = [],
  absencesByTechnician = {},
  isOperationInProgress = false,
  onEditOnCall // Nouvelle prop pour éditer une astreinte
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  // Calculer les absences en conflit pour cette période (mémorisé)
  const techniciansWithAbsences = useMemo(() => {
    const absencesMap = {};
    
    for (const technicianId of Object.keys(technicians)) {
      const technicianIdNumber = parseInt(technicianId, 10);
      const absences = absencesByTechnician[technicianIdNumber] || [];
      
      if (absences.length > 0) {
        // Utiliser la même logique que validateOnCall : 
        // vérifier la période complète d'astreinte (vendredi-jeudi)
        const onCallInterval = {
          start: weekData.startDate,  // vendredi
          end: weekData.endDate       // jeudi suivant
        };

        const conflictingAbsences = absences.filter(absence => {
          const absenceInterval = {
            start: new Date(absence.startDate),
            end: new Date(absence.endDate)
          };
          
          // Même logique que dans onCallValidation.js
          return areIntervalsOverlapping(onCallInterval, absenceInterval, { inclusive: true });
        });

        if (conflictingAbsences.length > 0) {
          absencesMap[technicianId] = conflictingAbsences;
        }
      }
    }
    
    return absencesMap;
  }, [technicians, weekData, absencesByTechnician]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAssign = (technicianId) => {
    // Le bypass est maintenant géré au niveau des absences, pas des astreintes
    onAssign(weekData.weekNumber, skillId, technicianId);
    handleClose();
  };

  const handleRemoveSpecific = (technicianId) => {
    onRemove(weekData.weekNumber, skillId, technicianId);
  };

  // Nouvelle fonction pour éditer une astreinte
  const handleEditOnCall = (technicianId) => {
    // 🔧 CORRECTION : Formatter les dates locales correctement
    const startDateStr = formatLocalDateToISO(weekData.startDate);
    const endDateStr = formatLocalDateToISO(weekData.endDate);
    
    // Trouver l'astreinte correspondante pour ce technicien et cette période
    // 🔧 CORRECTION : Extraire uniquement la partie date (YYYY-MM-DD) pour la comparaison
    // car les dates en base sont au format ISO complet (2026-01-02T17:00:00.000Z)
    const onCall = onCalls.find(oc => {
      const onCallStartDate = typeof oc.startDate === 'string' ? oc.startDate.split('T')[0] : oc.startDate;
      const onCallEndDate = typeof oc.endDate === 'string' ? oc.endDate.split('T')[0] : oc.endDate;
      
      return oc.technicianId === technicianId &&
        onCallStartDate === startDateStr &&
        onCallEndDate === endDateStr;
    });
    
    if (onCall && onEditOnCall) {
      onEditOnCall(onCall);
    }
  };

  // Créer un map des compétences pour un accès rapide
  const skillsMap = skills.reduce((acc, skill) => {
    acc[skill.id] = skill;
    return acc;
  }, {});

  // Trouver tous les techniciens assignés à cette compétence pour cette période
  const getAssignedTechnicians = () => {
    const assignedTechIds = [];
    
    // Chercher dans les astreintes en base de données
    onCalls.forEach(onCall => {
      const onCallStart = new Date(onCall.startDate);
      const onCallEnd = new Date(onCall.endDate);
      const weekStart = weekData.startDate;
      const weekEnd = weekData.endDate;
      
      // Vérifier si l'astreinte couvre cette période et cette compétence
      if (onCall.skills && Array.isArray(onCall.skills) && onCall.skills.includes(skillId) &&
          ((onCallStart <= weekStart && onCallEnd >= weekStart) ||
           (onCallStart <= weekEnd && onCallEnd >= weekEnd) ||
           (onCallStart >= weekStart && onCallEnd <= weekEnd))) {
        if (!assignedTechIds.includes(onCall.technicianId)) {
          assignedTechIds.push(onCall.technicianId);
        }
      }
    });

    return assignedTechIds.map(id => technicians[id]).filter(Boolean);
  };

  // Filtrer les techniciens qui ont cette compétence et ne sont pas déjà assignés
  const assignedTechnicians = getAssignedTechnicians();
  const assignedTechIds = assignedTechnicians.map(tech => tech.id);
  
  // Trouver si la compétence est critique
  const currentSkill = skills.find(s => s.id === skillId);
  const isSkillCritical = currentSkill && currentSkill.critical;
  
  const eligibleTechnicians = Object.values(technicians).filter(tech => {
    if (!assignedTechIds.includes(tech.id)) {
      // Vérifier si le technicien a cette compétence (nouveau ou ancien format)
      return tech.skills.some(skill => {
        let hasSkill = false;
        let skillLevel = 1;
        
        if (typeof skill === 'object' && skill.skillId) {
          hasSkill = skill.skillId === skillId;
          skillLevel = skill.level || 1;
        } else {
          hasSkill = skill === skillId;
          // Pas d'info de niveau, considérer niveau 1
          skillLevel = 1;
        }
        
        // Si la compétence est trouvée, vérifier le niveau si elle est critique
        if (hasSkill && isSkillCritical) {
          // Pour les compétences critiques, le technicien doit avoir niveau 2
          return skillLevel >= 2;
        }
        
        return hasSkill;
      });
    }
    return false;
  });

  const renderTechnicianSkills = (technician) => {
    // Gérer le nouveau format avec niveaux et l'ancien format pour compatibilité
    const techSkillsWithLevels = Array.isArray(technician.skills) 
      ? technician.skills.map(skill => {
          if (typeof skill === 'object' && skill.skillId) {
            // Nouveau format avec niveaux
            return { 
              skill: skillsMap[skill.skillId], 
              level: skill.level || 1 
            };
          } else {
            // Ancien format simple (compatibilité)
            return { 
              skill: skillsMap[skill], 
              level: 1 
            };
          }
        }).filter(item => item.skill)
      : [];

    const sortedSkills = techSkillsWithLevels.sort((a, b) => 
      a.skill.name.localeCompare(b.skill.name)
    );

    return (
      <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
        {sortedSkills.map((item) => (
          <Chip
            key={item.skill.id}
            label={`${item.skill.name} - ${getSkillLevelShortLabel(item.level)}`}
            size="small"
            sx={{
              fontSize: '0.65rem',
              height: '18px',
              backgroundColor: `${item.skill.color}20`,
              border: `2px solid ${getSkillLevelColor(item.level)}`,
              color: 'text.primary',
              '& .MuiChip-label': {
                px: 0.5,
                fontSize: '0.6rem',
                fontWeight: 'bold'
              }
            }}
          />
        ))}
      </Stack>
    );
  };

  const renderAbsenceInfo = (technicianId) => {
    const absences = techniciansWithAbsences[technicianId];
    if (!absences || absences.length === 0) return null;

    const isJTLOnly = absences.every(absence => absence.type === 'JTL');
    const hasBlocking = absences.some(absence => {
      const absenceType = absence.type?.toUpperCase();
      return ['CP', 'CA', 'MALADIE', 'FORMATION', 'CM', 'TPS', 'RECUP', 'MEDICAL', 'AUTRE'].includes(absenceType);
    });
    const hasFormation = absences.some(absence => absence.type === 'FORMATION');
    const hasAbsencesWithBypass = absences.some(absence => absence.bypassAbsenceValidation === true);
    
    // Convertir les types d'absence avec les labels corrects et gérer le bypass
    const absenceLabels = absences.map(absence => {
      let label;
      switch(absence.type?.toUpperCase()) {
        case 'CP':
        case 'CA': label = 'CA'; break;
        case 'JTL': label = 'JTL'; break;
        case 'MALADIE': label = 'Arrêt maladie'; break;
        case 'FORMATION': label = 'Formation'; break;
        case 'CM': label = 'CM'; break;
        case 'TPS': label = 'Temps partiel'; break;
        case 'RECUP': label = 'Récupération'; break;
        case 'RECUP_ASTREINTE': label = 'Récup astreinte'; break;
        case 'MEDICAL': label = 'Visite médicale'; break;
        case 'AUTRE': label = 'Autre'; break;
        default: label = absence.type || 'Inconnu';
      }
      
      // Ajouter l'indication de bypass si présente
      if (absence.bypassAbsenceValidation) {
        label += ' (BYPASS)';
      }
      
      return label;
    });
    
    const uniqueLabels = [...new Set(absenceLabels)].join(', ');

    // Déterminer la couleur selon le type d'absence et le bypass
    let chipColor = "default";
    let labelText = `Absent - ${uniqueLabels}`;
    
    if (hasAbsencesWithBypass) {
      chipColor = "success";
      labelText = uniqueLabels; // Le bypass est déjà dans le label
    } else if (hasBlocking) {
      chipColor = "error";
      labelText = `${uniqueLabels} - NON DISPONIBLE`;
    } else if (hasFormation && !isJTLOnly) {
      chipColor = "info";
    } else if (isJTLOnly) {
      chipColor = "warning";
    } else if (hasFormation) {
      chipColor = "warning"; // Formation + JTL = orange (JTL prime)
    }

    return (
      <Box sx={{ mt: 0.5 }}>
        <Chip
          label={labelText}
          size="small"
          color={chipColor}
          variant="outlined"
          sx={{
            fontSize: '0.65rem',
            height: '18px',
            '& .MuiChip-label': {
              px: 0.5,
              fontSize: '0.65rem'
            }
          }}
        />
      </Box>
    );
  };

  const getTechnicianMenuItemStyle = (technicianId) => {
    const absences = techniciansWithAbsences[technicianId];
    const hasAbsences = absences && absences.length > 0;
    
    if (!hasAbsences) {
      return {};
    }
    
    const isJTLOnly = absences.every(absence => absence.type === 'JTL');
    const hasBlocking = absences.some(absence => {
      const absenceType = absence.type?.toUpperCase();
      return ['CP', 'CA', 'MALADIE', 'FORMATION', 'CM', 'TPS', 'RECUP', 'MEDICAL', 'AUTRE'].includes(absenceType);
    });
    const hasFormation = absences.some(absence => absence.type === 'FORMATION');
    const hasAbsencesWithBypass = absences.some(absence => absence.bypassAbsenceValidation === true);
    
    if (hasAbsencesWithBypass) {
      // Bypass activé au niveau des absences - vert clair pour indiquer que c'est autorisé
      return {
        backgroundColor: 'success.light',
        '&:hover': {
          backgroundColor: 'success.main'
        }
      };
    }
    
    if (hasBlocking) {
      // Absences bloquantes sans bypass - grisé et non cliquable
      return {
        opacity: 0.5,
        backgroundColor: 'grey.100',
        pointerEvents: 'none',
        cursor: 'not-allowed'
      };
    } else if (hasFormation && !isJTLOnly) {
      // Formation (sans JTL) - bleu clair mais cliquable avec avertissement
      return {
        backgroundColor: 'info.light',
        '&:hover': {
          backgroundColor: 'info.main'
        }
      };
    } else if (isJTLOnly) {
      // JTL seulement - orange mais cliquable
      return {
        backgroundColor: 'warning.light',
        '&:hover': {
          backgroundColor: 'warning.main'
        }
      };
    } else if (hasFormation) {
      // Formation + JTL - orange (JTL prime)
      return {
        backgroundColor: 'warning.light',
        '&:hover': {
          backgroundColor: 'warning.main'
        }
      };
    }
    
    return {};
  };

  if (assignedTechnicians.length === 0) {
    // Aucun technicien assigné - afficher le bouton d'ajout
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '32px',
          border: '1px dashed',
          borderColor: 'grey.300',
          borderRadius: 1,
          '&:hover': {
            bgcolor: 'grey.50',
          }
        }}
      >
        <IconButton 
          size="small" 
          onClick={handleClick}
          disabled={eligibleTechnicians.length === 0 || isOperationInProgress}
          sx={{ 
            p: 0.5,
            opacity: isOperationInProgress ? 0.5 : 1
          }}
        >
          <PersonAddIcon fontSize="small" />
        </IconButton>

        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          PaperProps={{
            sx: { maxWidth: 300 }
          }}
        >
          {eligibleTechnicians.map((tech) => {
            const absences = techniciansWithAbsences[tech.id];
            const hasAbsences = absences && absences.length > 0;
            
            // Déterminer si le technicien peut être cliqué selon le type d'absence et bypass
            let canClick = true;
            let requiresBypass = false;
            
            if (hasAbsences) {
              // Vérifier s'il y a des absences avec bypass activé
              const hasAbsencesWithBypass = absences.some(absence => absence.bypassAbsenceValidation === true);
              
              if (hasAbsencesWithBypass) {
                // Si au moins une absence a le bypass, le technicien est cliquable
                canClick = true;
                requiresBypass = false;
              } else {
                // Vérifier les types d'absence bloquants sans bypass
                const hasBlockingWithoutBypass = absences.some(absence => {
                  const absenceType = absence.type?.toUpperCase();
                  const isBlocking = ['CP', 'CA', 'MALADIE', 'FORMATION', 'CM', 'TPS', 'RECUP', 'MEDICAL', 'AUTRE'].includes(absenceType);
                  return isBlocking && !absence.bypassAbsenceValidation;
                });
                
                if (hasBlockingWithoutBypass) {
                  // Absences bloquantes sans bypass - pas sélectionnable pour l'instant
                  // (On pourrait ajouter une option pour permettre la création du bypass ici)
                  canClick = false;
                  requiresBypass = false;
                } else {
                  // Seulement des absences non bloquantes ou avec bypass
                  canClick = true;
                  requiresBypass = false;
                }
              }
            }

            return (
              <MenuItem 
                key={tech.id} 
                onClick={canClick ? () => handleAssign(tech.id) : undefined}
                sx={{ 
                  flexDirection: 'column', 
                  alignItems: 'flex-start',
                  py: 1,
                  minWidth: 250,
                  ...getTechnicianMenuItemStyle(tech.id)
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                  {tech.firstName} {tech.lastName}
                </Typography>
                {renderTechnicianSkills(tech)}
                {renderAbsenceInfo(tech.id)}
              </MenuItem>
            );
          })}
        </Menu>
      </Box>
    );
  }

  // Technicien(s) assigné(s) - afficher avec boutons d'édition et suppression
  return (
    <Box sx={{ width: '100%', p: 0.5 }}>
      <Stack direction="column" spacing={0.5}>
        {assignedTechnicians.map((tech) => {
          // Trouver le niveau du technicien pour cette compétence
          const techSkill = tech.skills?.find(s => 
            (typeof s === 'object' && s.skillId === skillId) || s === skillId
          );
          const level = typeof techSkill === 'object' ? techSkill.level : 1;
          const levelLabel = level === 2 ? 'N2' : 'N1';
          
          return (
          <Box key={tech.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Chip
              label={
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 0.5,
                  width: '100%',
                  py: 0.25
                }}>
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    lineHeight: 1.1,
                    flex: 1,
                    textAlign: 'center'
                  }}>
                    <Typography sx={{ fontSize: '0.65rem', fontWeight: 500 }}>
                      {tech.firstName}
                    </Typography>
                    <Typography sx={{ fontSize: '0.65rem', fontWeight: 500 }}>
                      {tech.lastName}
                    </Typography>
                  </Box>
                  <Typography sx={{ 
                    fontSize: '0.6rem', 
                    fontWeight: 700,
                    color: level === 2 ? '#fff' : 'rgba(255,255,255,0.8)',
                    backgroundColor: level === 2 ? 'success.main' : 'info.main',
                    borderRadius: '4px',
                    px: 0.4,
                    py: 0.2,
                    minWidth: '20px',
                    textAlign: 'center'
                  }}>
                    {levelLabel}
                  </Typography>
                </Box>
              }
              size="small"
              variant="filled"
              color="primary"
              sx={{
                fontSize: '0.7rem',
                height: 'auto',
                minHeight: '32px',
                opacity: isOperationInProgress ? 0.7 : 1,
                flex: 1,
                '& .MuiChip-label': {
                  px: 0.5,
                  py: 0.25,
                  width: '100%',
                  display: 'block'
                }
              }}
            />
            
            {/* Bouton d'édition */}
            {onEditOnCall && (
              <Tooltip title="Modifier cette astreinte">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => handleEditOnCall(tech.id)}
                    disabled={isOperationInProgress}
                    sx={{
                      p: 0.25,
                      opacity: isOperationInProgress ? 0.5 : 1,
                      '&:hover': {
                        backgroundColor: 'action.hover'
                      }
                    }}
                  >
                    <EditIcon sx={{ fontSize: '0.75rem', color: 'text.secondary' }} />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            
            {/* Bouton de suppression */}
            <Tooltip title="Retirer de cette compétence">
              <span>
                <IconButton
                  size="small"
                  onClick={() => handleRemoveSpecific(tech.id)}
                  disabled={isOperationInProgress}
                  sx={{
                    p: 0.25,
                    opacity: isOperationInProgress ? 0.5 : 1,
                    '&:hover': {
                      backgroundColor: 'action.hover'
                    }
                  }}
                >
                  <DeleteIcon sx={{ fontSize: '0.75rem', color: 'error.main' }} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          );
        })}
      </Stack>
    </Box>
  );
};

export default React.memo(TechnicianAssignment); 